<template>
  <el-dialog
    :visible="visible"
    :title="dialogTitle"
    width="820px"
    append-to-body
    :close-on-click-modal="false"
    :show-close="!submitting"
    @update:visible="handleVisibleChange"
    @closed="handleClosed"
  >
    <div v-loading="loading" class="mapping-delete">
      <b-alert v-if="errorText" show variant="danger" class="mb-3">
        {{ errorText }}
      </b-alert>

      <!-- 步驟 3：正式刪除完成 -->
      <template v-if="result">
        <b-alert show variant="success" class="mb-2">
          重定向映射已刪除（物理刪除、無撤銷）。
        </b-alert>
        <ul class="mb-2">
          <li>舊號 → 新號：{{ deletedText }}</li>
          <li>
            變更記錄 id（audit_id）：<strong>{{ auditIdText }}</strong>
          </li>
        </ul>
        <p class="mb-1">notes：</p>
        <ul v-if="resultNotes.length > 0" class="mb-3">
          <li v-for="(note, index) in resultNotes" :key="index">{{ note }}</li>
        </ul>
        <p v-else class="text-muted">後端未回傳 notes。</p>
        <div class="text-right">
          <b-button size="sm" variant="primary" @click="handleVisibleChange(false)">
            關閉
          </b-button>
        </div>
      </template>

      <!-- 步驟 1 / 2：dry_run 預覽 → 填 reason → 二次確認 → 正式刪除 -->
      <template v-else-if="preview">
        <b-alert show variant="warning" class="mb-2">
          以下是 dry_run=1 預覽，尚未寫入任何資料。確認刪除後為物理刪除、無撤銷，
          唯一痕跡是 action=重定向 / change_type=刪除 的變更記錄。
        </b-alert>

        <h6 class="mb-1">將刪除的映射（deleted）</h6>
        <b-table
          bordered
          small
          striped
          :items="deletedRows"
          :fields="itemFields"
          class="mb-3"
        />

        <h6 class="mb-1">影響（impact）</h6>
        <ul class="mb-3">
          <li>
            舊號剩餘票房：
            <strong :class="hasImpactEngagements ? 'text-danger' : ''">
              {{ impactEngagementText }}
            </strong>
            <span v-if="impactDatesText" class="text-muted">
              （{{ impactDatesText }}）
            </span>
          </li>
          <li>
            新號台帳是否存在：{{ yesNo(impact.to_record_exists) }}
            <span v-if="impact.to_record_exists !== true" class="text-danger">
              （已是死規則）
            </span>
          </li>
          <li>
            舊號台帳是否存在：{{ yesNo(impact.from_ledger_exists) }}
            <span v-if="impact.from_ledger_exists === true" class="text-warning">
              （可能是雙向映射，請確認哪一邊才是目標）
            </span>
          </li>
        </ul>

        <p class="mb-1">notes：</p>
        <ul v-if="previewNotes.length > 0" class="mb-3">
          <li v-for="(note, index) in previewNotes" :key="index">{{ note }}</li>
        </ul>
        <p v-else class="text-muted">後端未回傳 notes。</p>

        <b-form-group
          label="刪除緣由（必填，寫入 action=重定向 / change_type=刪除 的變更記錄）"
          class="mb-2"
        >
          <b-form-input
            v-model="reason"
            size="sm"
            placeholder="例：舊號已不再使用，清理過期重定向映射"
          />
        </b-form-group>
        <b-form-checkbox v-model="confirmed" class="mb-3">
          我已確認：這是物理刪除、無撤銷；刪除後續 XML 若繼續發舊號將不再按映射解析（歷史票房不受影響）。
        </b-form-checkbox>

        <div class="text-right">
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-1"
            :disabled="submitting"
            @click="handleVisibleChange(false)"
          >
            取消
          </b-button>
          <b-button
            size="sm"
            variant="danger"
            :disabled="!canSubmit"
            @click="submit"
          >
            確認刪除
          </b-button>
        </div>
      </template>

      <p v-else-if="!loading" class="text-muted">沒有可預覽的映射。</p>
    </div>
  </el-dialog>
</template>

<script>
import { Message } from 'element-ui'
import { deleteManualFixMapping } from '@/api/posId'
import {
  POS_ID_READ_ONLY_HINT,
  buildMappingDeleteBody,
  buildMappingDeleteQuery,
  formatNumber,
  mappingEngagementSummary,
  normalizeNotes,
  resolvePosIdError
} from '@/utils/posId'

function hasText(value) {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

function textOrDash(value) {
  return hasText(value) ? String(value) : '—'
}

function objectOrEmpty(value) {
  return value && typeof value === 'object' ? value : {}
}

/**
 * P6 刪除流程（guide §5.3 / §5.4 / §6 通用規則 1）：
 *  1) 開啟時先 DELETE ?dry_run=1 預覽（零寫入、audit_id=null）；
 *  2) 顯示 deleted 四要素 + impact（N 條 / $M / K 人次、to_record_exists、
 *     from_ledger_exists）+ notes[] 逐條原樣（normalizeNotes）；
 *  3) reason 必填 + 二次確認勾選後才可「確認刪除」；
 *  4) 正式 DELETE 不帶 dry_run → 顯示 notes[] + audit_id，並 emit('deleted') 讓父層刷新列表；
 *  5) 錯誤：resolvePosIdError；10010 → 提示後關閉並刷新；403 無 code → 切唯讀。
 *
 * ⚠️ 正式刪除是物理刪除、無撤銷（測試時只准對合成資料做）。
 */
export default {
  name: 'MappingDeleteDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    record: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      errorText: '',
      preview: null,
      result: null,
      reason: '',
      confirmed: false,
      itemFields: [
        { key: 'item', label: '項目', thStyle: { width: '250px' } },
        { key: 'value', label: '內容' }
      ]
    }
  },
  computed: {
    dialogTitle() {
      if (this.result) {
        return '刪除重定向映射 · 完成'
      }
      if (this.preview) {
        return '刪除重定向映射 · 預覽與確認'
      }
      return '刪除重定向映射'
    },
    recordId() {
      return this.record && hasText(this.record.id) ? this.record.id : null
    },
    deletedInfo() {
      if (this.result) {
        return objectOrEmpty(this.result.deleted)
      }
      if (this.preview) {
        return objectOrEmpty(this.preview.deleted)
      }
      return this.record || {}
    },
    deletedText() {
      const item = this.deletedInfo
      const from = textOrDash(item.from_pos_id)
      const to = textOrDash(item.to_pos_id)
      return `${from} → ${to}`
    },
    deletedRows() {
      const item = this.deletedInfo
      return [
        { item: '記錄 id', value: textOrDash(item.id) },
        { item: '院線（from_circuit_id）', value: textOrDash(item.from_circuit_id) },
        { item: '舊號（from_pos_id）', value: textOrDash(item.from_pos_id) },
        { item: '新號（to_pos_id）', value: textOrDash(item.to_pos_id) },
        { item: '標題（from_title_name）', value: textOrDash(item.from_title_name) }
      ]
    },
    impact() {
      const value = this.preview ? this.preview.impact : null
      return value && typeof value === 'object' ? value : {}
    },
    impactSummary() {
      return mappingEngagementSummary(this.impact.from_engagements)
    },
    hasImpactEngagements() {
      return this.impactSummary.finalsCount > 0
    },
    impactEngagementText() {
      const summary = this.impactSummary
      const count = formatNumber(summary.finalsCount)
      const gross = this.moneyText(summary.gross)
      const admissions = formatNumber(summary.admissions)
      return `${count} 條 / ${gross} / ${admissions} 人次`
    },
    impactDatesText() {
      const summary = this.impactSummary
      if (!hasText(summary.dateFrom) && !hasText(summary.dateTo)) {
        return ''
      }
      const start = hasText(summary.dateFrom) ? summary.dateFrom : '不限'
      const end = hasText(summary.dateTo) ? summary.dateTo : '不限'
      return `${start} ~ ${end}`
    },
    previewNotes() {
      return normalizeNotes(this.preview ? this.preview.notes : [])
    },
    resultNotes() {
      return normalizeNotes(this.result ? this.result.notes : [])
    },
    auditIdText() {
      if (!this.result) {
        return '—'
      }
      const auditId = this.result.audit_id
      if (auditId === null || auditId === undefined || auditId === '') {
        return '（後端未回傳）'
      }
      return String(auditId)
    },
    canSubmit() {
      return (
        this.preview !== null &&
        this.loading !== true &&
        this.submitting !== true &&
        hasText(this.reason) &&
        this.confirmed === true
      )
    }
  },
  watch: {
    visible(value) {
      if (value === true) {
        this.startPreview()
      }
    },
    record() {
      if (this.visible === true) {
        this.startPreview()
      }
    }
  },
  methods: {
    formatNumber,
    textOrDash,
    yesNo(value) {
      return value === true ? '是' : '否'
    },
    moneyText(value) {
      return `$${formatNumber(value)}`
    },
    handleVisibleChange(value) {
      if (this.submitting === true && value === false) {
        return
      }
      this.$emit('update:visible', value === true)
    },
    handleClosed() {
      this.loading = false
      this.submitting = false
      this.errorText = ''
      this.preview = null
      this.result = null
      this.reason = ''
      this.confirmed = false
    },
    /** 步驟 1：dry_run=1 預覽（零寫入、audit_id=null） */
    startPreview() {
      if (this.loading === true || !hasText(this.recordId)) {
        return
      }
      this.loading = true
      this.errorText = ''
      this.preview = null
      this.result = null
      this.reason = ''
      this.confirmed = false
      deleteManualFixMapping(
        this.recordId,
        buildMappingDeleteQuery(true),
        buildMappingDeleteBody('')
      )
        .then((res) => {
          this.loading = false
          this.preview = res && res.data ? res.data : null
        })
        .catch((error) => {
          this.loading = false
          this.handleError(error)
        })
    },
    /** 步驟 4：正式刪除（不帶 dry_run；物理刪除、無撤銷） */
    submit() {
      if (this.canSubmit !== true) {
        return
      }
      this.submitting = true
      this.errorText = ''
      deleteManualFixMapping(
        this.recordId,
        buildMappingDeleteQuery(false),
        buildMappingDeleteBody(this.reason)
      )
        .then((res) => {
          this.submitting = false
          this.result = res && res.data ? res.data : null
          this.$emit('deleted')
        })
        .catch((error) => {
          this.submitting = false
          this.handleError(error)
        })
    },
    /** 10010 → 提示後關閉並刷新；403 無 code → 切唯讀並隱藏刪除入口 */
    handleError(error) {
      const info = resolvePosIdError(error)
      if (info.code === 10010) {
        Message.info('該筆重定向映射已不存在（可能已被刪除），列表已重新整理')
        this.$emit('update:visible', false)
        this.$emit('missing')
        return
      }
      if (info.isPermissionDenied) {
        Message.warning(POS_ID_READ_ONLY_HINT)
        this.$emit('read-only')
        this.$emit('update:visible', false)
        return
      }
      this.errorText = info.message
      Message.error(info.message)
    }
  }
}
</script>

<style lang="scss" scoped>
.mapping-delete {
  h6 {
    color: #0e72bc;
    font-weight: 500;
  }
  ::v-deep table {
    table-layout: fixed;
  }
  ::v-deep td,
  ::v-deep th {
    white-space: normal;
    word-break: break-word;
    vertical-align: top;
  }
}
</style>
